import { IAttributionData, IAttributionDetails } from '../models/models';

export type { IAttributionData, IAttributionDetails };

const STORAGE_KEY = 'bl_attribution';
const COOKIE_NAME = 'bl_attr';

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

function parseQueryString(qs: string): Record<string, string> {
  const result: Record<string, string> = {};
  if (!qs) return result;
  const cleanQs = qs.startsWith('?') || qs.startsWith('#') ? qs.substring(1) : qs;
  const pairs = cleanQs.split('&');
  for (const pair of pairs) {
    if (!pair) continue;
    const [rawKey, rawVal] = pair.split('=');
    if (rawKey) {
      try {
        result[decodeURIComponent(rawKey).trim()] = rawVal !== undefined ? decodeURIComponent(rawVal).trim() : '';
      } catch {
        result[rawKey.trim()] = rawVal !== undefined ? rawVal.trim() : '';
      }
    }
  }
  return result;
}

export function parseAttributionFromCookie(rawCookie: string | null): IAttributionData | null {
  if (!rawCookie) return null;
  let trimmed = rawCookie.trim();
  if (!trimmed) return null;

  // Strip wrapping double quotes if cookie was serialized with quotes
  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) {
    trimmed = trimmed.substring(1, trimmed.length - 1).trim();
  }

  // Try parsing as JSON first
  if (trimmed.startsWith('{') || trimmed.startsWith('%7B')) {
    try {
      const decoded = trimmed.startsWith('%') ? decodeURIComponent(trimmed) : trimmed;
      const parsed = JSON.parse(decoded);
      if (typeof parsed === 'object' && parsed !== null) {
        return normalizeAttributionObject(parsed);
      }
    } catch {
      // Not valid JSON, fallback to query string
    }
  }

  // Try parsing as query string: e.g. "fv_source=google&utm_source=google&gclid=123"
  const params = parseQueryString(trimmed);
  if (Object.keys(params).length > 0) {
    return extractAttributionFromParams(params);
  }

  return null;
}

function normalizeAttributionObject(obj: any): IAttributionData {
  const firstVisit: IAttributionDetails = obj.first_visit || obj.firstVisit || {};
  const latestClick: IAttributionDetails = obj.latest_click || obj.latestClick || {};

  // Check if flat keys exist
  const fv: IAttributionDetails = {
    source: firstVisit.source || obj.ft_source || obj.fv_source || obj.first_source || obj.first_visit_source,
    medium: firstVisit.medium || obj.ft_medium || obj.fv_medium || obj.first_medium || obj.first_visit_medium,
    campaign: firstVisit.campaign || obj.ft_campaign || obj.fv_campaign || obj.first_campaign || obj.first_visit_campaign,
    term: firstVisit.term || obj.ft_term || obj.fv_term || obj.first_term || obj.first_visit_term,
    content: firstVisit.content || obj.ft_content || obj.fv_content || obj.first_content || obj.first_visit_content,
    landing_url: firstVisit.landing_url || firstVisit.landingUrl || obj.landing_url || obj.fv_landing_url,
    referrer: firstVisit.referrer || obj.ext_referrer || obj.fv_referrer || obj.referrer,
    ga_client_id: firstVisit.ga_client_id || firstVisit.gaClientId || obj.ga_client_id || obj.fv_ga_client_id || obj.client_id,
  };

  const lc: IAttributionDetails = {
    source: latestClick.source || obj.utm_source || obj.lc_source || obj.latest_source,
    medium: latestClick.medium || obj.utm_medium || obj.lc_medium || obj.latest_medium,
    campaign: latestClick.campaign || obj.utm_campaign || obj.lc_campaign || obj.latest_campaign,
    term: latestClick.term || obj.utm_term || obj.lc_term || obj.latest_term,
    content: latestClick.content || obj.utm_content || obj.lc_content || obj.latest_content,
    gclid: latestClick.gclid || obj.gclid || obj.lc_gclid,
    gbraid: latestClick.gbraid || obj.gbraid || obj.lc_gbraid,
    wbraid: latestClick.wbraid || obj.wbraid || obj.lc_wbraid,
  };

  const cleanFv = cleanObject(fv);
  const cleanLc = cleanObject(lc);

  const result: IAttributionData = {};
  if (Object.keys(cleanFv).length > 0) result.first_visit = cleanFv;
  if (Object.keys(cleanLc).length > 0) result.latest_click = cleanLc;
  if (cleanLc.gclid) result.gclid = cleanLc.gclid;
  if (cleanLc.source) result.utm_source = cleanLc.source;
  if (cleanLc.medium) result.utm_medium = cleanLc.medium;
  if (cleanLc.campaign) result.utm_campaign = cleanLc.campaign;
  if (cleanLc.term) result.utm_term = cleanLc.term;
  if (cleanLc.content) result.utm_content = cleanLc.content;

  return result;
}

function extractAttributionFromParams(params: Record<string, string>): IAttributionData {
  const fv: IAttributionDetails = {
    source: params.ft_source || params.fv_source || params.first_source || params.first_visit_source,
    medium: params.ft_medium || params.fv_medium || params.first_medium || params.first_visit_medium,
    campaign: params.ft_campaign || params.fv_campaign || params.first_campaign || params.first_visit_campaign,
    term: params.ft_term || params.fv_term || params.first_term || params.first_visit_term,
    content: params.ft_content || params.fv_content || params.first_content || params.first_visit_content,
    landing_url: params.landing_url || params.fv_landing_url || params.landingUrl,
    referrer: params.ext_referrer || params.fv_referrer || params.referrer,
    ga_client_id: params.ga_client_id || params.fv_ga_client_id || params.gaClientId || params.client_id,
  };

  const lc: IAttributionDetails = {
    source: params.utm_source || params.lc_source || params.latest_source || params.latest_ad_source,
    medium: params.utm_medium || params.lc_medium || params.latest_medium || params.latest_ad_medium,
    campaign: params.utm_campaign || params.lc_campaign || params.latest_campaign || params.latest_ad_campaign,
    term: params.utm_term || params.lc_term || params.latest_term || params.latest_ad_term,
    content: params.utm_content || params.lc_content || params.latest_content || params.latest_ad_content,
    gclid: params.gclid || params.lc_gclid || params.latest_gclid,
    gbraid: params.gbraid || params.lc_gbraid || params.latest_gbraid,
    wbraid: params.wbraid || params.lc_wbraid || params.latest_wbraid,
  };

  const hasExplicitFv = Boolean(
    params.ft_source || params.fv_source || params.first_source || params.first_visit_source ||
    params.ft_medium || params.fv_medium || params.first_medium || params.first_visit_medium ||
    params.ft_campaign || params.fv_campaign || params.first_campaign || params.first_visit_campaign ||
    params.ft_term || params.fv_term || params.first_term || params.first_visit_term ||
    params.ft_content || params.fv_content || params.first_content || params.first_visit_content ||
    params.landing_url || params.fv_landing_url || params.landingUrl ||
    params.ext_referrer || params.fv_referrer || params.referrer ||
    params.ga_client_id || params.fv_ga_client_id || params.gaClientId || params.client_id
  );

  // If fv fields are not explicitly set with ft_* / fv_* prefix, but standard utm_* / gclid exist,
  // we synthesize first_visit only as a fallback for initial visits
  if (!hasExplicitFv) {
    if (lc.source) fv.source = lc.source;
    if (lc.medium) fv.medium = lc.medium;
    if (lc.campaign) fv.campaign = lc.campaign;
    if (lc.term) fv.term = lc.term;
    if (lc.content) fv.content = lc.content;
  }

  const cleanFv = cleanObject(fv);
  const cleanLc = cleanObject(lc);

  const result: IAttributionData = {};
  if (Object.keys(cleanFv).length > 0) result.first_visit = cleanFv;
  if (Object.keys(cleanLc).length > 0) result.latest_click = cleanLc;
  if (cleanLc.gclid) result.gclid = cleanLc.gclid;
  if (cleanLc.source) result.utm_source = cleanLc.source;
  if (cleanLc.medium) result.utm_medium = cleanLc.medium;
  if (cleanLc.campaign) result.utm_campaign = cleanLc.campaign;
  if (cleanLc.term) result.utm_term = cleanLc.term;
  if (cleanLc.content) result.utm_content = cleanLc.content;

  return result;
}

function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      const str = String(v).trim();
      cleaned[k as keyof T] = (str.length > 300 ? str.substring(0, 300) : str) as any;
    }
  }
  return cleaned;
}

export function parseAttributionFromUrl(searchString?: string): IAttributionData | null {
  if (typeof window === 'undefined') return null;
  const search = searchString !== undefined ? searchString : window.location.search;
  const params = parseQueryString(search);
  
  // Also check hash for query params if search is empty (e.g. #/signup?utm_source=...)
  if (Object.keys(params).length === 0 && window.location.hash.includes('?')) {
    const hashQuery = window.location.hash.split('?')[1];
    Object.assign(params, parseQueryString(hashQuery));
  }

  const attribution = extractAttributionFromParams(params);
  if (attribution.first_visit || attribution.latest_click) {
    return attribution;
  }
  return null;
}

export function getStoredAttribution(): IAttributionData | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Error reading stored attribution', e);
  }
  return null;
}

export function saveStoredAttribution(data: IAttributionData): void {
  if (typeof window === 'undefined') return;
  try {
    const str = JSON.stringify(data);
    sessionStorage.setItem(STORAGE_KEY, str);
    localStorage.setItem(STORAGE_KEY, str);
  } catch (e) {
    console.warn('Error saving attribution to storage', e);
  }
}

/**
 * Initializes and syncs attribution data:
 * 1. Checks current URL parameters.
 * 2. If missing, checks `bl_attr` cookie.
 * 3. Preserves `first_visit` (never overwritten).
 * 4. Updates `latest_click` only if new paid/campaign click parameters arrive.
 * 5. Saves merged attribution in storage for sign-up.
 */
export function initAttribution(): IAttributionData | null {
  if (typeof window === 'undefined') return null;

  const urlAttr = parseAttributionFromUrl();
  const cookieAttr = parseAttributionFromCookie(getCookie(COOKIE_NAME));
  const storedAttr = getStoredAttribution();

  let firstVisit = storedAttr?.first_visit;
  let latestClick = storedAttr?.latest_click;

  // 1. Establish First Visit if not already saved:
  // URL params take priority, then bl_attr cookie, then fallback
  if (!firstVisit) {
    firstVisit = cookieAttr?.first_visit || urlAttr?.first_visit;
  }

  // 2. Establish or update Latest Click:
  // If URL has ad/campaign click data (gclid, gbraid, wbraid or utm parameters):
  const isPaidOrAdClick = (attr?: IAttributionData | null) => {
    if (!attr?.latest_click) return false;
    const lc = attr.latest_click;
    return !!(lc.gclid || lc.gbraid || lc.wbraid || lc.source || lc.campaign || lc.medium);
  };

  if (isPaidOrAdClick(urlAttr)) {
    latestClick = urlAttr?.latest_click;
  } else if (!latestClick && isPaidOrAdClick(cookieAttr)) {
    latestClick = cookieAttr?.latest_click;
  }

  // Combine into unified attribution payload
  const combined: IAttributionData = {};
  if (firstVisit && Object.keys(firstVisit).length > 0) {
    combined.first_visit = firstVisit;
  }
  if (latestClick && Object.keys(latestClick).length > 0) {
    combined.latest_click = latestClick;
    if (latestClick.gclid) combined.gclid = latestClick.gclid;
    if (latestClick.source) combined.utm_source = latestClick.source;
    if (latestClick.medium) combined.utm_medium = latestClick.medium;
    if (latestClick.campaign) combined.utm_campaign = latestClick.campaign;
    if (latestClick.term) combined.utm_term = latestClick.term;
    if (latestClick.content) combined.utm_content = latestClick.content;
  }

  if (combined.first_visit || combined.latest_click) {
    saveStoredAttribution(combined);
    return combined;
  }

  return storedAttr;
}
